import { useMutation, useQuery, useQueryClient } from 'react-query';
import { getPet, getPetAvatarOptions, getActivePets, updatePetProfile } from 'api/petApi';
import { useHistory, useLocation, useParams } from 'react-router';
import React, { useContext, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import AnalyticsContext from 'context/AnalyticsContext';
import { PetInterface } from 'types/pet';
import PageLoader from 'components/loaders/PageLoader';
import { getPetClinics } from 'api/vetClinicApi';
import { Clinics } from '../../components/pet-form/VetClinic/vetClinicResponse';
import { retryCount } from '../../util/reactQueryOptions';
import { PetFormProvider } from '../pet-form/PetFormContext/PetFormContext';
import { ACCOUNT_OVERVIEW_PATH, getPetProfilePath } from '../../util/config';
import { handleAuthError } from '../../util/auth';
import log from '../../util/log';
import FetchError from '../../types/http';
import '../pet-form/PetForm/PetForm.scss';
import useLocationQuery from '../../hooks/useLocationQuery';
import {
  ApiPetProfile,
  ApiPetProfileStatusEnum,
} from '../../api/generated-client/PetProfile-Service-A.v1/models';
import { conditionalHistoryPush } from '../../util/helpers';
import EditFormConfiguredV2 from './EditForm/EditFormConfiguredV2';

interface PetEditMutation {
  payload: ApiPetProfile;
  image?: string;
}

export default function PetProfileEditConfigured() {
  const [_, setActivePet] = useState(false);
  const { petId } = useParams<{ petId: string }>();

  const queryClient = useQueryClient();
  const analytics = useContext(AnalyticsContext);
  let query = useLocationQuery();
  const redirectUrl = query.get('redirectUrl');
  const newPet = query.get('newPet');
  const history = useHistory();
  const location = useLocation();

  const handleNavigation = () => {
    if (!redirectUrl) {
      conditionalHistoryPush(getPetProfilePath(petId), history);
    } else {
      window.location.href = decodeURIComponent(redirectUrl);
    }
  };

  const { data: pet, error: petError } = useQuery<PetInterface, FetchError>(
    [getPet.key, petId],
    getPet,
    { retry: retryCount },
  );

  const { data: clinics, error: clinicsError, isLoading: clinicsLoading } = useQuery<
    Clinics,
    FetchError
  >([getPetClinics.key, petId], getPetClinics, {
    retry: retryCount,
  });

  const invalidateQueries = () => {
    const invalidQueries = [
      queryClient.removeQueries(getActivePets.key),
      queryClient.removeQueries([getPet.key, petId]),
    ];
    return Promise.all(invalidQueries);
  };

  useEffect(() => {
    // NOTE: If pet is recently deleted and we return to Edit page, pet object will be undefined. Redirect to accounts page in this case.
    const inactivePet = pet?.status === ApiPetProfileStatusEnum.Inactive;
    if (newPet !== null) {
      return;
    }
    if (inactivePet) {
      window.location.href = ACCOUNT_OVERVIEW_PATH;
    } else if (!pet && !newPet !== true) {
      window.location.href = ACCOUNT_OVERVIEW_PATH;
    } else setActivePet(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [petId, pet]);

  const { isError: loadingAvatarOptionsError, data: petAvatarOptionsData } = useQuery(
    [getPetAvatarOptions.key, pet?.petType?.id],
    getPetAvatarOptions,
    {
      enabled: !!pet,
      retry: retryCount,
    },
  );

  const updatePet = useMutation(
    ({ payload }: PetEditMutation) => updatePetProfile(Number(petId), payload),
    {
      onSuccess: () => {
        invalidateQueries()
          .catch((error) => {
            log.error('Failed to invalidate cache on update pet profile:', error);
          })
          .finally(() => {
            analytics.trackEvent('new-pet-profile', 'edit', 'save-changes', null, {
              sourceView: 'pet-profile-v2',
              petId: petId,
            });
            handleNavigation();
          });
      },
      onError: (error: FetchError) => {
        log.error('Failed to Update Pet Profile:', `Status code: ${error.status}`, error);
        handleAuthError(error.status, location.pathname);
      },
    },
  );

  const showPetForm = !!pet && !updatePet.isLoading && !clinicsLoading;

  return (
    <div data-testid="pet-profile-form-wrapper" className="pet-profile-pet-form-wrapper">
      <Helmet>
        <title>Edit Pet | Chewy.com</title>
      </Helmet>
      {showPetForm ? (
        <PetFormProvider
          petProfile={pet}
          avatarOptions={petAvatarOptionsData}
          loadingAvatarOptionsError={loadingAvatarOptionsError}
        >
          <EditFormConfiguredV2 handleNavigation={handleNavigation} clinics={clinics || []} />
        </PetFormProvider>
      ) : (
        <PageLoader />
      )}
    </div>
  );
}
